
<div class="page-header">
  <div>
    <h1>Unternehmer</h1>
    <p>Übersicht über angeschlossene Unternehmer und deren Fahrer.</p>
  </div>
</div>

<section class="panel section-stack">
  <div class="panel-heading">
    <div>
      <h2>Unternehmensübersicht</h2>
      <p>Suche nach Unternehmern, Email-Ansprechpartnern oder Kontaktdaten innerhalb deines Zugriffsbereichs.</p>
    </div>

    <button type="button" class="primary-button" (click)="openCreateDialog()">Unternehmer anlegen</button>
  </div>

  <div class="filter-bar">
    <label>
      <span>Suche</span>
      <input
        type="search"
        placeholder="Unternehmer, Email-Ansprechpartner oder E-Mail"
        [value]="search()"
        (input)="setSearch($event)"
        (keydown.enter)="load()">
    </label>

    <label>
      <span>Status</span>
      <select [value]="status()" (change)="setStatus($event)">
        <option value="">Alle</option>
        <option value="ACTIVE">Aktiv</option>
        <option value="TEST">Test</option>
        <option value="INACTIVE">Inaktiv</option>
      </select>
    </label>

    <button type="button" class="primary-button" (click)="load()">Filtern</button>
    <button type="button" class="ghost-button" (click)="reset()">Zurücksetzen</button>
  </div>

  @if (companies(); as page) {
    <div class="company-table-shell">
      <table class="table company-table">
      <thead>
        <tr>
          <th>Unternehmer</th>
          <th>Email-Ansprechpartner</th>
          <th>E-Mail</th>
          <th>Telefon</th>
          <th>Fahrzeuge</th>
          <th>Fahrer</th>
          <th>Ø Fortschritt</th>
          <th>Ø Scoring</th>
          <th>Kritisch/offen</th>
          <th>Aktion</th>
        </tr>
      </thead>
      <tbody>
        @for (company of page.items; track company.id) {
          <tr>
            <td data-label="Unternehmer">
              <strong>{{ company.name }}</strong><br>
              <span>{{ company.shortCode || company.organizationName }}</span>
            </td>
            <td data-label="Email-Ansprechpartner">{{ valueOrDash(company.contactName) }}</td>
            <td data-label="E-Mail">{{ valueOrDash(company.contactEmail) }}</td>
            <td data-label="Telefon">{{ valueOrDash(company.contactPhone) }}</td>
            <td data-label="Fahrzeuge">{{ company.vehicleCount ?? '-' }}</td>
            <td data-label="Fahrer">{{ company.driverCount }}</td>
            <td data-label="Fortschritt">
              <div class="company-progress-cell">
                <strong>{{ company.averageProgressPercent }} %</strong>
                <span class="company-progress-track">
                  <span [style.width.%]="company.averageProgressPercent"></span>
                </span>
              </div>
            </td>
            <td data-label="Scoring">{{ formatScore(company.averageScorePercent) }}</td>
            <td data-label="Kritisch/offen">
              <div class="company-critical-cell">
                <strong>{{ company.criticalDrivers }}</strong>
                <span>/ {{ company.openEnrollments }} offen</span>
              </div>
            </td>
            <td data-label="Aktion">
              <div class="company-action-stack">
                <a class="ghost-button" [routerLink]="['/companies', company.id]">Öffnen</a>
                @if (canDelete()) {
                  <button type="button" class="ghost-button danger-button" (click)="deleteCompany(company)">Löschen</button>
                } @else {
                  <span>-</span>
                }
              </div>
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="11">Keine Unternehmer gefunden.</td>
          </tr>
        }
      </tbody>
      </table>
    </div>
    @if (deleteError()) {
      <p class="field-error">{{ deleteError() }}</p>
    }
  } @else {
    <div class="empty">Unternehmer werden geladen.</div>
  }
</section>

@if (createDialogOpen()) {
  <div class="modal-backdrop" role="presentation" (click)="closeCreateDialog()">
    <section class="modal-panel" role="dialog" aria-modal="true" aria-labelledby="create-company-title" (click)="$event.stopPropagation()">
      <div class="panel-heading">
        <div>
          <h2 id="create-company-title">Unternehmer anlegen</h2>
          <p>Email-Ansprechpartner, Adresse und Fahrzeuganzahl erfassen.</p>
        </div>
        <button type="button" class="ghost-button" (click)="closeCreateDialog()">Schließen</button>
      </div>

      <form class="dialog-form" (submit)="createCompany($event)">
        @if (canSelectOrganization()) {
          <label class="full-width">
            <span>Organisation</span>
            <select required [value]="form().organizationId ?? ''" (change)="setOrganization($event)">
              <option value="">Organisation auswählen</option>
              @for (organization of organizationOptions(); track organization.id) {
                <option [value]="organization.id">{{ organization.name }}</option>
              }
            </select>
          </label>
        }

        <label>
          <span>Unternehmer / Firmenname</span>
          <input type="text" required [value]="form().name" (input)="updateForm('name', $event)">
        </label>

        <label>
          <span>Kürzel</span>
          <input type="text" [value]="form().shortCode" (input)="updateForm('shortCode', $event)" placeholder="z. B. RIT">
        </label>

        <label>
          <span>Email-Ansprechpartner</span>
          <input type="text" [value]="form().contactName" (input)="updateForm('contactName', $event)">
        </label>

        <label>
          <span>Straße</span>
          <input type="text" [value]="form().street" (input)="updateForm('street', $event)">
        </label>

        <label>
          <span>PLZ</span>
          <input type="text" [value]="form().postalCode" (input)="updateForm('postalCode', $event)">
        </label>

        <label>
          <span>Ort</span>
          <input type="text" [value]="form().city" (input)="updateForm('city', $event)">
        </label>

        <label>
          <span>E-Mail</span>
          <input type="email" [value]="form().contactEmail" (input)="updateForm('contactEmail', $event)">
        </label>

        <label>
          <span>Telefon</span>
          <input type="tel" [value]="form().contactPhone" (input)="updateForm('contactPhone', $event)">
        </label>

        <label>
          <span>Anzahl Fahrzeuge</span>
          <input type="number" min="0" [value]="form().vehicleCount ?? ''" (input)="updateNumberForm('vehicleCount', $event)">
        </label>

        @if (createError()) {
          <p class="field-error full-width">{{ createError() }}</p>
        }

        <div class="dialog-actions full-width">
          <button type="submit" class="primary-button" [disabled]="saving()">
            {{ saving() ? 'Wird angelegt...' : 'Unternehmer anlegen' }}
          </button>
          <button type="button" class="ghost-button" (click)="closeCreateDialog()">Abbrechen</button>
        </div>
      </form>
    </section>
  </div>
}
  
